import React, { createContext, useContext, useMemo } from 'react';
import type { MappingLevel } from '../types/mappingDrillDown';
import { useMappingStackStore } from '../store/mappingStackStore';

export interface MappingDrillDownContextValue {
  useDrillDown: boolean;
  effectiveTab?: string;
  pushLevel: (level: Omit<MappingLevel, 'id'> & { id?: string }) => void;
}

const defaultValue: MappingDrillDownContextValue = {
  useDrillDown: false,
  effectiveTab: undefined,
  pushLevel: () => {},
};

export const MappingDrillDownContext = createContext<MappingDrillDownContextValue>(defaultValue);

export function useMappingDrillDown(): MappingDrillDownContextValue {
  return useContext(MappingDrillDownContext);
}

interface MappingDrillDownProviderProps {
  useDrillDown: boolean;
  effectiveTab?: string;
  children: React.ReactNode;
}

export function MappingDrillDownProvider({
  useDrillDown,
  effectiveTab,
  children,
}: MappingDrillDownProviderProps) {
  const pushLevel = useMappingStackStore(s => s.pushLevel);
  const value = useMemo(
    () => ({
      useDrillDown,
      effectiveTab,
      pushLevel: (level: Omit<MappingLevel, 'id'> & { id?: string }) => {
        const id = level.id ?? `level-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        pushLevel({ ...level, id });
      },
    }),
    [useDrillDown, effectiveTab, pushLevel],
  );
  return <MappingDrillDownContext.Provider value={value}>{children}</MappingDrillDownContext.Provider>;
}
